<template>
  <v-progress-linear v-if="loading" indeterminate />
  <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>
  <v-btn v-if="error" :disabled="loading || disabled" @click="load"
    >Retry donation options</v-btn
  >
  <div class="donation-fields">
    <div class="donation-funding">
      <v-select
        v-model="fund"
        :items="funds"
        item-title="fundName"
        item-value="fundId"
        label="Donation fund"
        :disabled="disabled || loading"
        required
      />
      <v-select
        v-model="initiative"
        :items="initiatives"
        item-title="initiativeName"
        item-value="initiativeId"
        label="Donation initiative"
        :disabled="disabled || loading"
        required
      />
    </div>
    <v-text-field
      v-model="inMemoryOf"
      class="donation-full-width"
      label="In memory or in honor of"
      maxlength="500"
      :disabled="disabled"
      clearable
    />
    <v-text-field
      v-model="restriction"
      class="donation-full-width"
      label="Donation restriction"
      maxlength="200"
      :disabled="disabled"
      clearable
    />
    <v-textarea
      class="donation-full-width"
      v-model="description"
      label="Donation description"
      maxlength="500"
      :disabled="disabled"
      clearable
    />
    <div class="donation-full-width donation-options">
      <v-checkbox
        v-model="isMatch"
        label="Matching donation"
        :disabled="disabled"
        hide-details
      />
      <v-checkbox
        v-model="inKind"
        label="In-kind donation"
        :disabled="disabled"
        hide-details
      />
      <v-checkbox
        v-model="skipAck"
        label="Skip acknowledgement"
        :disabled="disabled"
        hide-details
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import { useDonationService } from "../../../cbi-generator/frontend/composables/useDonationFrontendService";
import type { DonationFundStaff } from "../../../cbi-generator/generated/dto/DonationFundStaff";
import type { DonationInitiativeStaff } from "../../../cbi-generator/generated/dto/DonationInitiativeStaff";
import type { DonationPurchaseDetails } from "../../../cbi-generator/generated/dto/DonationPurchaseDetails";
import { useNotificationStore } from "@/stores/notification";

const props = defineProps<{
  disabled: boolean;
  modelValue: DonationPurchaseDetails | null;
  /** Settings to start from, e.g. when reconfiguring a cart donation. */
  initial?: DonationPurchaseDetails | null;
}>();
const emit = defineEmits<{
  "update:modelValue": [value: DonationPurchaseDetails | null];
}>();
const funds = shallowRef<DonationFundStaff[]>([]);
const initiatives = shallowRef<DonationInitiativeStaff[]>([]);
const fund = ref<number | null>(null);
const initiative = ref<number | null>(null);
const inMemoryOf = ref<string | null>(null);
const restriction = ref<string | null>(null);
const description = ref<string | null>(null);
const isMatch = ref(false);
const inKind = ref(false);
const skipAck = ref(false);
const loading = ref(false);
const error = ref("");
let disposed = false;
const notification = useNotificationStore();
onBeforeUnmount(() => {
  disposed = true;
});

async function load() {
  loading.value = true;
  error.value = "";
  fund.value = null;
  initiative.value = null;
  funds.value = [];
  initiatives.value = [];
  emit("update:modelValue", null);
  try {
    const service = useDonationService();
    const [loadedFunds, loadedInitiatives] = await Promise.all([
      service.getAllDonationFundStaffs(),
      service.getAllDonationInitiativeStaffs(),
    ]);
    if (disposed) return;
    funds.value = loadedFunds
      .filter((item) => item.active === "Y")
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    initiatives.value = loadedInitiatives
      .filter((item) => item.active === "Y")
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    if (!funds.value.length || !initiatives.value.length)
      throw new Error("No active donation fund or initiative is available.");
    const initial = props.initial;
    if (initial) {
      if (funds.value.some((item) => item.fundId === initial.fund.fundId))
        fund.value = initial.fund.fundId;
      if (
        initiatives.value.some(
          (item) => item.initiativeId === initial.initiative.initiativeId,
        )
      )
        initiative.value = initial.initiative.initiativeId;
      inMemoryOf.value = initial.inMemoryOf;
      restriction.value = initial.restriction;
      description.value = initial.description;
      isMatch.value = initial.isMatch;
      inKind.value = initial.inKind;
      skipAck.value = initial.skipAck;
    }
  } catch (failure) {
    if (disposed) return;
    error.value =
      failure instanceof Error
        ? failure.message
        : "Unable to load donation options.";
    notification.addSnackbar(error.value, "error");
  } finally {
    if (!disposed) loading.value = false;
  }
}
onMounted(load);
watch(
  [
    fund,
    initiative,
    inMemoryOf,
    restriction,
    description,
    isMatch,
    inKind,
    skipAck,
    loading,
    error,
  ],
  () => {
    emit(
      "update:modelValue",
      fund.value != null &&
        initiative.value != null &&
        !loading.value &&
        !error.value
        ? {
            fund: { fundId: fund.value },
            initiative: { initiativeId: initiative.value },
            inMemoryOf: inMemoryOf.value,
            restriction: restriction.value,
            description: description.value,
            isMatch: isMatch.value,
            inKind: inKind.value,
            skipAck: skipAck.value,
          }
        : null,
    );
  },
);
</script>

<style scoped>
.donation-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-block: 1rem;
}

.donation-funding {
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(min(100%, max(14rem, calc((100% - 16px) / 2))), 1fr)
  );
  column-gap: 16px;
}

.donation-funding > * {
  min-width: 0;
}

.donation-fields > * {
  min-width: 0;
}

.donation-full-width {
  width: 100%;
}

.donation-options {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 1rem;
}
</style>
